import React, { useEffect, useState } from "react";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { Button } from "@/components/ui/button";
import { Download, X, Smartphone } from "lucide-react";

/**
 * InstallPrompt — subtle bottom-sheet that appears after a short delay,
 * once per 30 days, on supported platforms. Shows native install on Android,
 * iOS instructions modal trigger via the InstallAppButton on iOS.
 */
const SHOW_DELAY_MS = 6000;

export default function InstallPrompt() {
  const { canInstall, isIos, isStandalone, install, dismiss, dismissedRecently } =
    usePwaInstall();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isStandalone || dismissedRecently) return;
    if (!canInstall && !isIos) return;
    const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [canInstall, isIos, isStandalone, dismissedRecently]);

  if (!open || isStandalone) return null;

  const close = () => {
    dismiss();
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 sm:bottom-6 sm:right-6 sm:left-auto sm:max-w-sm pointer-events-none"
      data-testid="install-prompt"
    >
      <div className="m-3 rounded-2xl bg-white border border-stone-200 shadow-xl p-4 pointer-events-auto fade-in">
        <div className="flex items-start gap-3">
          <img
            src="/apple-touch-icon.png"
            alt="COMPANIN"
            className="h-12 w-12 rounded-2xl shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-stone-900">
              Get COMPANIN on your home screen
            </div>
            <p className="text-xs text-stone-600 mt-0.5">
              Faster access, full-screen, works offline.
            </p>
          </div>
          <button
            onClick={close}
            className="text-stone-400 hover:text-stone-700 -mr-1 -mt-1 p-1"
            aria-label="Dismiss"
            data-testid="install-prompt-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 flex gap-2">
          {canInstall ? (
            <Button
              onClick={async () => {
                const result = await install();
                if (result.outcome !== "accepted") dismiss();
                setOpen(false);
              }}
              className="flex-1 rounded-full bg-[#4A5D4E] hover:bg-[#3B4A3E]"
              data-testid="install-prompt-install"
            >
              <Download className="h-4 w-4 mr-2" /> Install
            </Button>
          ) : (
            <a
              href="/profile"
              onClick={close}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white text-sm font-medium h-9 px-4"
              data-testid="install-prompt-instructions"
            >
              <Smartphone className="h-4 w-4" /> Show me how
            </a>
          )}
          <Button
            variant="outline"
            onClick={close}
            className="rounded-full border-stone-300"
            data-testid="install-prompt-later"
          >
            Later
          </Button>
        </div>
      </div>
    </div>
  );
}
